import React, { useEffect, useMemo, useState } from 'react';
import { Trade } from '../types';
import { MarketDataState } from '../hooks/useMarketData';
import useCollection from '../hooks/useCollection';
import * as Actions from '../firebase/actions';
import ConfirmationModal from './ConfirmationModal';

interface TradeJournalPageProps {
  marketData: MarketDataState;
  isAdmin: boolean;
  prefill?: Partial<Trade> | null;
  onClearPrefill?: () => void;
}

type TradeMarketType = NonNullable<Trade['marketType']>;

const RECENT_TRADE_LIMIT = 120;

const RESULT_LABEL: Record<Trade['result'], string> = {
  win: '✅ 승',
  loss: '❌ 패',
  be: '➖ 무',
};

const RESULT_CLASS: Record<Trade['result'], string> = {
  win: 'bg-green-100 text-green-700',
  loss: 'bg-red-100 text-red-700',
  be: 'bg-gray-100 text-gray-600',
};

const MARKET_TYPE_CLASS: Record<TradeMarketType, string> = {
  spot: 'bg-sky-100 text-sky-700',
  futures: 'bg-violet-100 text-violet-700',
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): Omit<Trade, 'id' | 'createdAt'> => ({
  date: today(),
  symbol: 'BTC',
  marketType: 'futures',
  side: 'long',
  result: 'win',
  funding: 0,
  oi: 0,
  oiTrend: 'neutral',
  liqTrend: 'neutral',
  entryPrice: undefined,
  exitPrice: undefined,
  note: '',
});

function fmtOI(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  return n.toFixed(0);
}

function getTradeMarketType(trade: Partial<Trade>): TradeMarketType {
  if (trade.marketType === 'spot' || trade.marketType === 'futures') {
    return trade.marketType;
  }

  if (
    trade.side === 'short' ||
    (trade.funding ?? 0) !== 0 ||
    (trade.oi ?? 0) !== 0 ||
    (trade.oiTrend && trade.oiTrend !== 'neutral') ||
    (trade.liqTrend && trade.liqTrend !== 'neutral')
  ) {
    return 'futures';
  }

  return 'spot';
}

function computeTradePnlPercent(trade: Trade): number | null {
  if (!Number.isFinite(trade.entryPrice) || !Number.isFinite(trade.exitPrice) || !trade.entryPrice || trade.entryPrice <= 0) {
    return null;
  }

  if (getTradeMarketType(trade) === 'spot') {
    return ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100;
  }

  if (trade.side === 'short') {
    return ((trade.entryPrice - trade.exitPrice) / trade.entryPrice) * 100;
  }

  return ((trade.exitPrice - trade.entryPrice) / trade.entryPrice) * 100;
}

function formatPnlPercent(value: number, count: number): string {
  if (count === 0) {
    return '—';
  }

  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function getPnlClassName(value: number, count: number): string {
  if (count === 0) {
    return 'text-gray-400';
  }

  if (value > 0) {
    return 'text-green-600';
  }

  if (value < 0) {
    return 'text-red-500';
  }

  return 'text-gray-500';
}

const TradeJournalPage: React.FC<TradeJournalPageProps> = ({ marketData, isAdmin, prefill, onClearPrefill }) => {
  const trades = useCollection<Trade>('trades', 'createdAt', 'desc');
  const [showForm, setShowForm] = useState(Boolean(prefill));
  const [form, setForm] = useState<Omit<Trade, 'id' | 'createdAt'>>(
    prefill
      ? { ...emptyForm(), ...prefill, marketType: getTradeMarketType(prefill) }
      : emptyForm()
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (!prefill || Object.keys(prefill).length === 0) {
      return;
    }

    setForm({
      ...emptyForm(),
      ...prefill,
      marketType: getTradeMarketType(prefill),
    });
    setEditingId(null);
    setShowForm(true);
  }, [prefill]);

  const normalizedTrades = useMemo(
    () => trades.map((trade) => ({ ...trade, marketType: getTradeMarketType(trade) })),
    [trades]
  );

  const stats = useMemo(() => {
    const total = normalizedTrades.length;
    const wins = normalizedTrades.filter((trade) => trade.result === 'win').length;
    const losses = normalizedTrades.filter((trade) => trade.result === 'loss').length;
    const bes = normalizedTrades.filter((trade) => trade.result === 'be').length;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
    return { total, wins, losses, bes, winRate };
  }, [normalizedTrades]);

  const recentPnl = useMemo(() => {
    return normalizedTrades.slice(0, RECENT_TRADE_LIMIT).reduce(
      (acc, trade) => {
        const pnl = computeTradePnlPercent(trade);
        if (pnl == null) {
          return acc;
        }

        const marketType = getTradeMarketType(trade);
        acc[marketType] += pnl;
        acc[`${marketType}Count`] += 1;
        return acc;
      },
      {
        spot: 0,
        futures: 0,
        spotCount: 0,
        futuresCount: 0,
      }
    );
  }, [normalizedTrades]);

  const setField = <K extends keyof typeof form>(key: K, value: typeof form[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAutoFill = () => {
    const snap = marketData.snapshots[form.symbol] ?? null;
    if (!snap) return;

    setForm((prev) => ({
      ...prev,
      funding: snap.funding,
      oi: snap.oi,
      oiTrend: snap.oiTrend,
      liqTrend: snap.liqTrend,
    }));
  };

  const handleOpenNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
    onClearPrefill?.();
  };

  const handleEdit = (trade: Trade) => {
    const { id, createdAt, ...rest } = trade;
    setEditingId(id);
    setForm({
      ...emptyForm(),
      ...rest,
      marketType: getTradeMarketType(trade),
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!isAdmin) return;

    setSaving(true);
    try {
      if (editingId) {
        await Actions.updateTrade(editingId, form);
      } else {
        await Actions.addTrade(form);
      }

      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      onClearPrefill?.();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!tradeToDelete) return;
    await Actions.deleteTrade(tradeToDelete);
    setTradeToDelete(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Trade Journal</h2>
          <p className="mt-0.5 text-sm text-gray-500">거래를 기록하고 엣지를 찾아보자</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleOpenNew}
            className="rounded-xl bg-fuchsia-500 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-fuchsia-600"
          >
            + 새 거래 기록
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-7">
        {[
          { label: '총 거래', value: stats.total, cls: 'text-gray-800' },
          { label: '승률', value: `${stats.winRate}%`, cls: stats.winRate >= 60 ? 'text-green-600' : stats.winRate >= 50 ? 'text-yellow-600' : 'text-red-500' },
          { label: '승', value: stats.wins, cls: 'text-green-600' },
          { label: '패', value: stats.losses, cls: 'text-red-500' },
          { label: '무', value: stats.bes, cls: 'text-gray-500' },
        ].map((item) => (
          <div key={item.label} className="rounded-xl border border-white/30 bg-white/60 p-3 text-center shadow backdrop-blur">
            <p className="text-xs text-gray-400">{item.label}</p>
            <p className={`text-xl font-bold ${item.cls}`}>{item.value}</p>
          </div>
        ))}

        <div className="col-span-2 rounded-xl border border-white/30 bg-white/60 p-3 shadow backdrop-blur">
          <p className="text-xs text-gray-400">최근 손익</p>
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-gray-500">현물</span>
              <span className={`text-sm font-bold ${getPnlClassName(recentPnl.spot, recentPnl.spotCount)}`}>
                {formatPnlPercent(recentPnl.spot, recentPnl.spotCount)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-gray-500">선물</span>
              <span className={`text-sm font-bold ${getPnlClassName(recentPnl.futures, recentPnl.futuresCount)}`}>
                {formatPnlPercent(recentPnl.futures, recentPnl.futuresCount)}
              </span>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-gray-400">최근 {RECENT_TRADE_LIMIT}건, 입출가 있는 거래만 집계</p>
        </div>
      </div>

      {showForm && isAdmin && (
        <div className="space-y-4 rounded-2xl border border-fuchsia-100 bg-white/70 p-5 shadow backdrop-blur">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-fuchsia-700">{editingId ? '거래 수정' : '새 거래 기록'}</h3>
            <button
              onClick={handleAutoFill}
              disabled={Object.keys(marketData.snapshots).length === 0}
              className="rounded-lg bg-fuchsia-50 px-3 py-1.5 text-xs font-semibold text-fuchsia-600 transition-colors hover:bg-fuchsia-100 disabled:opacity-40"
            >
              현재값 자동 채우기
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-gray-500">날짜</label>
              <input
                type="date"
                value={form.date}
                onChange={(event) => setField('date', event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">심볼</label>
              <select
                value={form.symbol}
                onChange={(event) => setField('symbol', event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">거래 타입</label>
              <select
                value={form.marketType}
                onChange={(event) => setField('marketType', event.target.value as TradeMarketType)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="spot">현물</option>
                <option value="futures">선물</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">방향</label>
              <select
                value={form.side}
                onChange={(event) => setField('side', event.target.value as Trade['side'])}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="long">롱</option>
                <option value="short">숏</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">펀딩비 (%)</label>
              <input
                type="number"
                step="0.0001"
                value={form.funding}
                onChange={(event) => setField('funding', parseFloat(event.target.value) || 0)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">OI (USD)</label>
              <input
                type="number"
                step="1000000"
                value={form.oi}
                onChange={(event) => setField('oi', parseFloat(event.target.value) || 0)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">OI 추세</label>
              <select
                value={form.oiTrend}
                onChange={(event) => setField('oiTrend', event.target.value as Trade['oiTrend'])}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="up">증가</option>
                <option value="down">감소</option>
                <option value="neutral">중립</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">Liq 추세</label>
              <select
                value={form.liqTrend}
                onChange={(event) => setField('liqTrend', event.target.value as Trade['liqTrend'])}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="up">증가</option>
                <option value="down">감소</option>
                <option value="neutral">중립</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">결과</label>
              <select
                value={form.result}
                onChange={(event) => setField('result', event.target.value as Trade['result'])}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              >
                <option value="win">승</option>
                <option value="loss">패</option>
                <option value="be">무</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">진입가 (선택)</label>
              <input
                type="number"
                value={form.entryPrice ?? ''}
                onChange={(event) => setField('entryPrice', event.target.value === '' ? undefined : parseFloat(event.target.value))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">청산가 (선택)</label>
              <input
                type="number"
                value={form.exitPrice ?? ''}
                onChange={(event) => setField('exitPrice', event.target.value === '' ? undefined : parseFloat(event.target.value))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>

            <div className="col-span-2 sm:col-span-3">
              <label className="mb-1 block text-xs text-gray-500">메모</label>
              <textarea
                value={form.note}
                onChange={(event) => setField('note', event.target.value)}
                rows={2}
                placeholder="진입 이유, 컨텍스트, 메모"
                className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                onClearPrefill?.();
              }}
              className="rounded-lg px-4 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-100"
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="rounded-lg bg-fuchsia-500 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-fuchsia-600 disabled:opacity-50"
            >
              {saving ? '저장 중...' : editingId ? '수정' : '기록 저장'}
            </button>
          </div>
        </div>
      )}

      {normalizedTrades.length === 0 ? (
        <div className="rounded-2xl border border-white/30 bg-white/60 p-12 text-center text-gray-400 shadow backdrop-blur">
          <p className="mb-3 text-4xl">📝</p>
          <p className="font-medium">기록된 거래가 없다</p>
          {isAdmin && <p className="mt-1 text-sm">첫 거래를 기록해봐</p>}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/30 bg-white/60 shadow backdrop-blur">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80 text-xs text-gray-500">
                  <th className="px-4 py-3 text-left font-semibold">날짜</th>
                  <th className="px-4 py-3 text-center font-semibold">심볼</th>
                  <th className="px-4 py-3 text-center font-semibold">타입</th>
                  <th className="px-4 py-3 text-center font-semibold">방향</th>
                  <th className="px-4 py-3 text-center font-semibold">펀딩비</th>
                  <th className="px-4 py-3 text-center font-semibold">OI</th>
                  <th className="px-4 py-3 text-center font-semibold">OI추세</th>
                  <th className="px-4 py-3 text-center font-semibold">Liq추세</th>
                  <th className="px-4 py-3 text-center font-semibold">결과</th>
                  <th className="px-4 py-3 text-left font-semibold">메모</th>
                  {isAdmin && <th className="px-4 py-3 text-center font-semibold">관리</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {normalizedTrades.map((trade) => (
                  <tr key={trade.id} className="transition-colors hover:bg-white/40">
                    <td className="px-4 py-3 text-xs text-gray-600">{trade.date}</td>
                    <td className="px-4 py-3 text-center font-bold text-gray-800">{trade.symbol}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${MARKET_TYPE_CLASS[getTradeMarketType(trade)]}`}>
                        {getTradeMarketType(trade) === 'spot' ? '현물' : '선물'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${trade.side === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {trade.side === 'long' ? '롱' : '숏'}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-center font-mono text-xs font-bold ${trade.funding > 0 ? 'text-orange-500' : trade.funding < 0 ? 'text-blue-500' : 'text-gray-500'}`}>
                      {trade.funding >= 0 ? '+' : ''}
                      {trade.funding.toFixed(4)}%
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs text-gray-500">{fmtOI(trade.oi)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={trade.oiTrend === 'up' ? 'text-green-500' : trade.oiTrend === 'down' ? 'text-red-400' : 'text-gray-300'}>
                        {trade.oiTrend === 'up' ? '▲' : trade.oiTrend === 'down' ? '▼' : '•'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={trade.liqTrend === 'up' ? 'text-green-500' : trade.liqTrend === 'down' ? 'text-red-400' : 'text-gray-300'}>
                        {trade.liqTrend === 'up' ? '▲' : trade.liqTrend === 'down' ? '▼' : '•'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${RESULT_CLASS[trade.result]}`}>
                        {RESULT_LABEL[trade.result]}
                      </span>
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-3 text-xs text-gray-500">{trade.note || '—'}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(trade)}
                            className="text-sm text-gray-300 transition-colors hover:text-fuchsia-500"
                          >
                            수정
                          </button>
                          <button
                            onClick={() => setTradeToDelete(trade.id)}
                            className="text-sm text-gray-300 transition-colors hover:text-red-400"
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(tradeToDelete)}
        onClose={() => setTradeToDelete(null)}
        onConfirm={handleDelete}
        title="거래 삭제"
        message="이 거래 기록을 삭제할까? 되돌릴 수 없다."
      />
    </div>
  );
};

export default TradeJournalPage;
